import React, { useState, useEffect } from 'react';
import { subscriptionService } from '../services/api';
import { useAuthStore } from '../store';
import { GlassButton, GlassCard } from '../components/GlassUI';
import { FEATURE_LABELS } from '../config/plans';
import { FaCheck, FaStar, FaCrown, FaGem } from 'react-icons/fa';

declare global {
  interface Window {
    Razorpay: any;
  }
}

interface Plan {
  id: string;
  name: string;
  price: number;
  finalPrice?: number;
  discountPercent?: number;
  description: string;
  maxCustomers: number;
  maxStaff: number;
  duration?: number;
  badge?: string | null;
  features?: string[];
}

const planIcons: Record<string, any> = {
  FREE: FaStar,
  BASIC: FaGem,
  PRO: FaCrown,
  ENTERPRISE: FaCrown,
};

const planColors: Record<string, string> = {
  FREE: 'from-slate-400 to-slate-300',
  BASIC: 'from-blue-500 to-blue-400',
  PRO: 'from-purple-600 to-purple-400',
  ENTERPRISE: 'from-amber-600 to-yellow-400',
};

export const PricingPage: React.FC<{ standalone?: boolean }> = ({ standalone = true }) => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [processing, setProcessing] = useState<string | null>(null);
  const { subscription, setSubscription } = useAuthStore();

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await subscriptionService.getPlans();
      setPlans(res.data.plans || []);
    } catch (error: any) {
      console.error('Error loading plans:', error);
      setLoadError(error?.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (plan: Plan) => {
    if (plan.id === 'FREE') return;

    setProcessing(plan.id);
    try {
      const orderRes = await subscriptionService.createOrder(plan.id);
      const { orderId, amount, currency, keyId, planName, simulated } = orderRes.data;

      // Simulated mode (no Razorpay keys) — activate instantly
      if (simulated) {
        const verifyRes = await subscriptionService.verifyPayment({
          razorpay_order_id: orderId,
          razorpay_payment_id: `sim_pay_${Date.now()}`,
          razorpay_signature: 'simulated',
          plan: plan.id,
          simulated: true,
        });
        if (verifyRes.data.success) {
          setSubscription({
            plan: plan.id,
            planName: plan.name,
            status: 'ACTIVE',
            endDate: verifyRes.data.endDate,
          });
          alert(`✅ Successfully activated the ${planName} plan (1 year)!`);
        }
        setProcessing(null);
        return;
      }

      const options = {
        key: keyId,
        amount: amount,
        currency: currency,
        name: 'ApniCRM',
        description: `${planName} Plan Subscription`,
        order_id: orderId,
        handler: async (response: any) => {
          const verifyRes = await subscriptionService.verifyPayment({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });

          if (verifyRes.data.success) {
            setSubscription({
              plan: plan.id,
              status: 'ACTIVE',
              planName: plan.name,
            });
            alert(`Successfully subscribed to ${planName} plan!`);
          }
        },
        prefill: {
          contact: '',
        },
        theme: {
          color: '#1e293b',
        },
        modal: {
          ondismiss: () => {
            setProcessing(null);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Payment initiation failed');
    } finally {
      setProcessing(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) return;
    setLoading(true);
    try {
      await subscriptionService.cancel();
      setSubscription({ ...subscription!, status: 'CANCELLED' });
      alert('Subscription cancelled.');
    } catch (error) {
      alert('Failed to cancel subscription');
    } finally {
      setLoading(false);
    }
  };

  const isSubscribedPlan = (planId: string) => {
    if (!subscription) return false;
    if (subscription.status === 'TRIAL' && planId === 'FREE') return true;
    return subscription.plan === planId && (subscription.status === 'ACTIVE' || subscription.status === 'TRIAL');
  };

  const content = (
    <>
      {standalone && (
        <div className='text-center mb-12'>
          <h1 className='text-4xl font-bold text-slate-900 mb-4'>Choose Your Plan</h1>
          {subscription?.status === 'TRIAL' && (
            <div className='inline-block bg-blue-50 border border-blue-200 rounded-xl px-6 py-3'>
              <p className='text-blue-800 font-medium'>
                Trial ends: {subscription.trialEnd ? new Date(subscription.trialEnd).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          )}
          {subscription?.status === 'ACTIVE' && (
            <p className='text-slate-600'>
              You are currently on the <strong>{subscription.planName || subscription.plan}</strong> plan.
              {subscription.endDate && ` Valid until ${new Date(subscription.endDate).toLocaleDateString()}`}
            </p>
          )}
          {subscription?.status === 'EXPIRED' && (
            <div className='inline-block bg-red-50 border border-red-200 rounded-xl px-6 py-3'>
              <p className='text-red-800 font-medium'>Your subscription has expired. Please choose a plan to continue.</p>
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className='text-center py-12'>
          <p className='text-slate-500 text-lg'>Loading plans...</p>
        </div>
      )}

      {loadError && (
        <div className='text-center py-12'>
          <p className='text-red-500 mb-4'>Failed to load plans. Check that the backend server is running.</p>
          <GlassButton variant='secondary' onClick={loadPlans}>Retry</GlassButton>
        </div>
      )}

      {!loading && !loadError && plans.length === 0 && (
        <div className='text-center py-12'>
          <p className='text-slate-500'>No plans available.</p>
          <GlassButton variant='secondary' onClick={loadPlans} className='mt-4'>Refresh</GlassButton>
        </div>
      )}

      {plans.length > 0 && (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
          {plans.map((plan) => {
            const Icon = planIcons[plan.id] || FaStar;
            const isCurrent = isSubscribedPlan(plan.id);
            const isFree = (plan.finalPrice ?? plan.price) === 0;

            return (
              <GlassCard
                key={plan.id}
                className={`relative overflow-hidden transition-all hover:shadow-2xl ${
                  isCurrent ? 'ring-2 ring-slate-900' : ''
                }`}
              >
                {isCurrent && (
                  <div className='absolute top-3 right-3 bg-slate-900 text-white text-xs px-3 py-1 rounded-full font-semibold'>
                    Current
                  </div>
                )}

                <div className={`bg-gradient-to-r ${planColors[plan.id] || 'from-slate-500 to-slate-700'} p-6 -m-4 mb-4 text-white`}>
                  <Icon className='text-3xl mb-2' />
                  <h3 className='text-2xl font-bold'>{plan.name}</h3>
                  <p className='text-white/80 text-sm mt-1'>{plan.description}</p>
                </div>

                <div className='mb-6'>
                  {isFree ? (
                    <div>
                      <span className='text-4xl font-bold text-slate-900'>Free</span>
                      <span className='text-slate-500'> / {plan.duration} days</span>
                    </div>
                  ) : (
                    <div>
                      <span className='text-4xl font-bold text-slate-900'>
                        ₹{(plan.finalPrice ?? plan.price).toLocaleString()}
                      </span>
                      <span className='text-slate-500'> / year</span>
                      {(plan.discountPercent ?? 0) > 0 && (
                        <p className='text-xs font-medium mt-1'>
                          <span className='text-slate-400 line-through mr-2'>₹{plan.price.toLocaleString()}</span>
                          <span className='text-emerald-600'>{plan.discountPercent ?? 0}% off</span>
                        </p>
                      )}
                      {subscription?.plan === plan.id && (
                        <p className='text-xs text-emerald-600 font-medium mt-1'>Renew yearly plan</p>
                      )}
                    </div>
                  )}
                </div>

                <ul className='space-y-3 mb-8'>
                  <li className='flex items-center gap-2 text-slate-700'>
                    <FaCheck className='text-green-500 flex-shrink-0' />
                    Up to {plan.maxCustomers === 999999 ? 'Unlimited' : plan.maxCustomers} customers
                  </li>
                  <li className='flex items-center gap-2 text-slate-700'>
                    <FaCheck className='text-green-500 flex-shrink-0' />
                    Up to {plan.maxStaff === 999 ? 'Unlimited' : plan.maxStaff} staff accounts
                  </li>
                  {(plan.features || []).map((f) => (
                    <li key={f} className='flex items-center gap-2 text-slate-700'>
                      <FaCheck className='text-green-500 flex-shrink-0' />
                      {FEATURE_LABELS[f] || f}
                    </li>
                  ))}
                </ul>

                {isCurrent && subscription?.status === 'ACTIVE' ? (
                  <GlassButton
                    variant='secondary'
                    className='w-full'
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel Subscription
                  </GlassButton>
                ) : isCurrent && subscription?.status === 'TRIAL' ? (
                  <div className='text-center text-slate-500 text-sm py-2'>
                    Active during trial
                  </div>
                ) : (
                  <GlassButton
                    variant={isFree ? 'secondary' : 'primary'}
                    className='w-full'
                    onClick={() => handleSubscribe(plan)}
                    disabled={processing !== null}
                  >
                    {processing === plan.id ? 'Processing...' : isFree ? 'Current Plan' : subscription?.status === 'EXPIRED' || subscription?.status === 'CANCELLED' ? 'Renew Now' : 'Subscribe Now'}
                  </GlassButton>
                )}
              </GlassCard>
            );
          })}
        </div>
      )}
    </>
  );

  if (standalone) {
    return (
      <div className='min-h-screen bg-slate-50 p-4 md:p-8'>
        <div className='max-w-6xl mx-auto'>
          {content}
        </div>
      </div>
    );
  }

  return content;
};

export default PricingPage;
